'use client';

import { useMemo } from 'react';
import { Carousel } from '@/app/components/shared/Carousel';
import { Button } from '@/app/components/ui/button';
import { cn, reportEvent } from '@/app/utils';
import { useRouter } from '@/i18n/routing';
import { GenerateNowText, TOOL_INFO } from '../constants';
import type { ToolConfig, ToolType } from '../types';

interface StepCardProps {
  title: string;
  description: string;
  image: string;
  index: number;
}

function StepCard({ title, description, image, index }: StepCardProps) {
  return (
    <div className="flex flex-col items-center overflow-hidden rounded-[24px] bg-white text-left md:h-full">
      <img
        src={image}
        alt={title}
        width={397}
        height={272}
        className="mb-6 w-full rounded-lgd-lg"
        loading="lazy"
        decoding="async"
      />
      <div className="flex w-full flex-1 flex-col p-[32px] pt-0 md:p-6 md:pt-0">
        <h3 className="mb-3 font-semibold text-[32px] leading-[1.4] md:text-[24px]">{title}</h3>
        <div className="flex flex-1 justify-between gap-[20px]">
          <p className="text-base text-gray-600 leading-[1.6] md:text-sm">{description}</p>
          <span className="translate-x-1 translate-y-[32px] self-end font-bold text-[#e4e4e4] text-[110px] leading-[1.4] sm:text-[90px]">
            {index + 1}
          </span>
        </div>
      </div>
    </div>
  );
}

type StepsProps = {
  toolKey: ToolType;
  toolConfig: ToolConfig;
};

export function Steps({ toolKey, toolConfig }: StepsProps) {
  const router = useRouter();
  const productInfo = TOOL_INFO[toolKey];

  const steps = productInfo.steps.list.map((step, index) => {
    return {
      image: toolConfig.steps[index],
      title: step.title,
      description: step.description,
    };
  });

  const stepsContent = useMemo(() => {
    return steps.map((step, index) => <StepCard key={step.title} index={index} {...step} />);
  }, [steps]);

  const handleGenerateNowClick = () => {
    reportEvent('productpage_generate', {
      element_name: `${toolKey} _generate`,
    });
    router.push(toolConfig.generateNow);
  };

  return (
    <section className="bg-[#F3F3F3]">
      <div className="mx-auto max-w-[1440px] px-[100px] py-[72px] text-center md:px-0 md:py-[40px]">
        <h2 className="mb-16 font-bold text-[40px] leading-[1.4] md:mb-8 md:font-semibold md:text-[28px]">
          {productInfo.steps.title}
        </h2>
        <div className="grid grid-cols-3 gap-[25px] md:hidden md:grid-cols-1">{stepsContent}</div>
        {/* Mobile View - Carousel */}
        <div className={cn('hidden md:block')}>
          <Carousel items={stepsContent} slideClassName="px-4" autoPlay={false} />
        </div>
        <Button variant="primary" size="xl" className="mt-8" onClick={handleGenerateNowClick}>
          {GenerateNowText}
        </Button>
      </div>
    </section>
  );
}
